import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ReceiptText } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ordersApi } from "@/lib/api";
import type { OrderDto, OrderStatus } from "@/lib/api/types";
import { cn, formatMoney, formatTime, titleCase } from "@/lib/utils";

const STATUS_OPTIONS: (OrderStatus | "ALL")[] = ["ALL", "OPEN", "SERVED", "PAID", "CANCELLED"];

/** Read-only order log for one restaurant — no payment or cancel actions here. */
export function OrdersTab({ tenantId }: { tenantId: number }) {
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "ALL">("ALL");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: ["orders", tenantId, statusFilter],
    queryFn: () =>
      ordersApi.list({ tenantId, ...(statusFilter === "ALL" ? {} : { status: statusFilter }) }),
  });

  const sorted = useMemo(
    () => (orders.data ?? []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [orders.data],
  );

  const selected = sorted.find((o) => o.id === selectedId) ?? null;

  return (
    <>
      <div className="mb-3 flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={cn(
              "rounded-[var(--radius-pill)] border px-3 py-1 text-xs font-semibold transition-colors",
              statusFilter === s
                ? "border-transparent bg-gradient-sunset text-primary-foreground shadow-glow"
                : "border-border bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            {s === "ALL" ? "All statuses" : titleCase(s)}
          </button>
        ))}
      </div>

      <Card>
        {orders.isLoading && <Skeleton className="m-5 h-64" />}
        {orders.data && sorted.length === 0 && (
          <EmptyState icon={ReceiptText} title="No orders" description="Nothing matches this filter yet." />
        )}
        {sorted.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Waiter</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((order) => (
                <TableRow key={order.id} className="cursor-pointer" onClick={() => setSelectedId(order.id)}>
                  <TableCell className="font-mono font-medium">{order.orderNumber}</TableCell>
                  <TableCell>{order.tableLabel}</TableCell>
                  <TableCell>{order.waiterName}</TableCell>
                  <TableCell>{formatTime(order.createdAt)}</TableCell>
                  <TableCell>{order.items.length}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{formatMoney(order.total)}</TableCell>
                  <TableCell>
                    <StatusBadge status={order.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        {selected && <OrderDetailDialog order={selected} />}
      </Dialog>
    </>
  );
}

function OrderDetailDialog({ order }: { order: OrderDto }) {
  return (
    <DialogContent className="max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 font-mono">{order.orderNumber}</DialogTitle>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{order.tableLabel}</span>
          <span>·</span>
          <span>{order.waiterName}</span>
          <span>·</span>
          <span>{formatTime(order.createdAt)}</span>
          <StatusBadge status={order.status} />
        </div>
      </DialogHeader>

      <div className="space-y-1.5 rounded-[var(--radius-md)] border border-border p-3">
        {order.items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate">
              <span className="font-semibold">{item.qty}×</span> {item.itemName}
              {item.note && <span className="text-muted-foreground"> — {item.note}</span>}
            </span>
            <StatusBadge status={item.status} />
            <span className="w-20 text-right font-semibold tabular-nums">{formatMoney(item.lineTotal)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-border pt-2 text-sm">
          <span className="font-medium text-muted-foreground">Total</span>
          <span className="text-lg font-bold">{formatMoney(order.total)}</span>
        </div>
        {order.note && <p className="text-xs italic text-muted-foreground">Note: {order.note}</p>}
        {order.cancelReason && <p className="text-xs text-destructive">Cancelled: {order.cancelReason}</p>}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Orders are read-only here — payments are recorded in the OSF app.
      </p>
    </DialogContent>
  );
}
